import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import * as React from "react";

import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { AssociationType } from "@/shared/domain/association.ts";
import { TopicRecord } from "@/shared/domain/topics";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import { SearchParams } from "@/shared/environment";
import { Goto } from "@/shared/navigation/Goto";
import { LoggerService } from "@/shared/services";
import { nanoSecToSecStr } from "@/shared/time";

const linkErrorComponent = (
  <Typography variant="body2" color="error">
    Failed to load link
  </Typography>
);

function defaultLinkName(topicRecord: TopicRecord) {
  return topicRecord.topic_name;
}

interface TopicVizLinkProps {
  topicId: string;
  getLinkName?: (topicRecord: TopicRecord) => string;

  /**
   * If provided, the file will be opened at the given time.
   * Otherwise, the file will be opened at the start of the file.
   *
   * `startTime` is in nanoseconds since the UNIX epoch.
   **/
  startTime?: bigint;
}

export const TopicVizLink: React.FC<TopicVizLinkProps> = ({
  topicId,
  getLinkName = defaultLinkName,
  startTime,
}) => {
  const topicQuery = useTopicById(topicId);

  if (topicQuery.isLoading) {
    return (
      <Typography variant="body2">
        <Skeleton variant="text" width="200px" />
      </Typography>
    );
  }

  if (topicQuery.isError) {
    LoggerService.error(topicQuery.error?.message, topicQuery.error);
    return linkErrorComponent;
  }

  if (topicQuery.data?.association.association_type !== AssociationType.File) {
    LoggerService.error("Can only link to topics associated with files.");
    return linkErrorComponent;
  }

  const fileId = topicQuery.data.association.association_id;

  // Build the visualize URL
  const searchParams = new URLSearchParams();
  searchParams.append("file", fileId);

  if (topicQuery.data?.topic_name) {
    searchParams.set("filterTerm", topicQuery.data.topic_name);
  }

  if (startTime) {
    searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(startTime));
  }

  return (
    <RobotoLinkHoverUnderline
      to={`${Goto.route.Visualize}?${searchParams}`}
      variant="body2"
    >
      {getLinkName(topicQuery.data)}
    </RobotoLinkHoverUnderline>
  );
};
